import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, MapPin, Clock, Users, ArrowLeft, Ticket, Pin, ListOrdered } from "lucide-react";
import { Header } from "@/components/Header";
import { VenueMap } from "@/components/VenueMap";
import { PhotoWall } from "@/components/PhotoWall";
import { EventFeedback } from "@/components/EventFeedback";
import { CalendarExport } from "@/components/CalendarExport";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function EventDetail() {
  const { id } = useParams();
  const { user, login } = useAuth();
  const [event, setEvent] = useState(null);

  useEffect(() => {
    apiFetch(`/events/${id}`).then(setEvent).catch(() => setEvent(false));
  }, [id]);

  const rsvp = async () => {
    if (!user) {
      toast("Sign in to RSVP");
      login();
      return;
    }
    const res = await apiFetch(`/events/${id}/rsvp`, { method: "POST" });
    setEvent((e) => ({
      ...e,
      rsvped: res.rsvped,
      rsvp_count: res.rsvp_count,
      waitlisted: res.waitlisted,
      waitlist_count: res.waitlist_count,
    }));
    if (res.waitlisted) toast(`You're #${res.waitlist_position} on the waitlist`);
    else if (res.rsvped) toast("You're going!");
    else if (res.promoted) toast("RSVP cancelled — the next person on the waitlist is in");
    else toast("You're off the list");
  };

  const togglePin = async () => {
    const res = await apiFetch(`/events/${id}/pin`, { method: "POST" });
    setEvent((e) => ({ ...e, pinned: res.pinned }));
    toast.success(res.pinned ? "Featured on the front page" : "Removed from the front page");
  };

  const save = async () => {    if (!user) {
      login();
      return;
    }
    const res = await apiFetch(`/events/${id}/save`, { method: "POST" });
    setEvent((e) => ({ ...e, saved: res.saved }));
  };

  if (event === false) return <div className="p-10">Event not found.</div>;
  if (!event) return <div className="min-h-screen bg-white" />;

  const start = new Date(event.start_at);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <Link
          to="/calendar"
          data-testid="back-link"
          className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 transition-colors duration-200 hover:text-neutral-900"
        >
          <ArrowLeft className="h-4 w-4" /> All events
        </Link>

        <h1 className="max-w-3xl text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl" data-testid="event-title">
          {event.title}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-600">
          <span className="font-semibold">{event.category}</span>
          <span className="flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" /> {event.venue_name}, {event.neighborhood}
          </span>
          <button
            onClick={save}
            data-testid="detail-save-btn"
            className="flex items-center gap-1.5 font-semibold text-neutral-900 underline decoration-neutral-300 transition-colors duration-200 hover:decoration-neutral-900"
          >
            <Heart className={`h-4 w-4 ${event.saved ? "fill-[#84CC16] text-[#4D7C0F]" : ""}`} />
            {event.saved ? "Saved" : "Save"}
          </button>
          {user?.role === "admin" && (
            <button
              onClick={togglePin}
              data-testid="pin-btn"
              className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold transition-colors duration-200 ${
                event.pinned ? "bg-[#B8FF2E] text-[#14210A]" : "border border-neutral-300 text-neutral-800 hover:bg-neutral-50"
              }`}
            >
              <Pin className="h-3.5 w-3.5" /> {event.pinned ? "Featured on front page" : "Feature on front page"}
            </button>
          )}
        </div>

        <div className="mt-6 overflow-hidden rounded-3xl bg-neutral-100">
          <img src={event.image_url} alt={event.title} className="h-[300px] w-full object-cover sm:h-[440px]" />
        </div>

        <div className="mt-10 grid gap-12 lg:grid-cols-[1fr_360px]">
          <div>
            <div className="flex flex-wrap gap-2">
              {event.tags?.map((t) => (
                <span key={t} className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold text-neutral-700">
                  {t}
                </span>
              ))}
            </div>
            <h2 className="mt-8 text-2xl font-bold tracking-tight text-neutral-900">About this event</h2>
            <p className="mt-3 whitespace-pre-line leading-relaxed text-neutral-700">{event.description}</p>

            {event.sponsors?.length > 0 && (
              <p className="mt-5 text-sm text-neutral-500" data-testid="sponsor-names">
                <span className="font-semibold text-neutral-700">Supported by:</span>{" "}
                {event.sponsors.map((s) => s.name).join(" · ")}
              </p>
            )}

            <div className="mt-10 border-t border-neutral-200 pt-8">
              <h2 className="text-2xl font-bold tracking-tight text-neutral-900">Hosted by</h2>
              <div className="mt-4 flex items-center gap-3">
                <span className="grid h-12 w-12 place-items-center rounded-full bg-neutral-900 text-base font-bold text-white">
                  {(event.host_name || "P")[0]}
                </span>
                <div>
                  <p className="font-semibold text-neutral-900">{event.host_name}</p>
                  <p className="text-sm text-neutral-500">{event.host_org}</p>
                </div>
              </div>
            </div>

            <div className="mt-10 border-t border-neutral-200 pt-8">
              <h2 className="text-2xl font-bold tracking-tight text-neutral-900">Where</h2>
              <p className="mt-2 text-neutral-700">{event.address}</p>
              <VenueMap event={event} />
            </div>

            <PhotoWall eventId={event.event_id} isPast={new Date(event.start_at) < new Date()} />
            <EventFeedback eventId={event.event_id} isPast={new Date(event.start_at) < new Date()} />

          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-[0_8px_28px_rgba(0,0,0,0.12)]">
              <p className="text-2xl font-extrabold text-neutral-900">{event.price}</p>
              <div className="mt-5 space-y-3 text-sm text-neutral-700">
                <p className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-neutral-400" />
                  {start.toLocaleString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </p>
                <p className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-neutral-400" />
                  <span data-testid="rsvp-count">{event.rsvp_count}</span> going · {event.capacity} spots
                </p>
                <p className="flex items-center gap-2">
                  <Ticket className="h-4 w-4 text-neutral-400" /> Community event, no ticket resale
                </p>
                {event.waitlist_count > 0 && (
                  <p className="flex items-center gap-2 font-semibold text-[#4D7C0F]" data-testid="waitlist-count">
                    <ListOrdered className="h-4 w-4" /> {event.waitlist_count} on the waitlist
                  </p>
                )}
              </div>
              <button
                onClick={rsvp}
                data-testid="rsvp-button"
                className={`mt-6 w-full rounded-full py-3 text-sm font-bold transition-colors duration-200 ${
                  event.rsvped || event.waitlisted
                    ? "border border-neutral-900 bg-white text-neutral-900 hover:bg-neutral-100"
                    : "bg-[#B8FF2E] text-[#14210A] hover:bg-[#A2E80B]"
                }`}
              >
                {event.rsvped
                  ? "You're going · Cancel RSVP"
                  : event.waitlisted
                    ? "On the waitlist · Leave"
                    : !user
                      ? "Sign in to RSVP"
                      : event.capacity && event.rsvp_count >= event.capacity
                        ? "Full · Join the waitlist"
                        : "RSVP"}
              </button>
              {!event.rsvped && !event.waitlisted && event.capacity > 0 && event.rsvp_count >= event.capacity && (
                <p className="mt-2 text-center text-xs text-neutral-500" data-testid="waitlist-hint">
                  If a spot opens, the first person in line is RSVP'd automatically and emailed.
                </p>
              )}
              <div className="mt-3">
                <CalendarExport event={event} />
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
